import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";

import { useGetSalesforceSyncLogsQuery } from "@/pages/Private/redux/salesforce/salesforce.api";

import { SalesforceEntityType } from "../schema/salesforce";
import {
	describeSyncLogError,
	isDuplicateRejection,
	statusColorFor,
} from "./salesforceSyncLog.utils";

const LIMIT = 20;

const formatDate = (dateStr: string): string =>
	new Date(dateStr).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});

const StatusBadge: FunctionComponent<{ status: string }> = ({ status }) => (
	<span
		className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColorFor(status)}`}
	>
		{status}
	</span>
);

/**
 * Sync history.
 *
 * The Error column shows Salesforce's own `errorCode` and the fields it named ahead of the
 * free-text message — see `salesforceSyncLog.utils.ts`. A duplicate-rule rejection additionally
 * gets a hint, because it is the one failure mode that is a customer *configuration* matter
 * rather than a mapping problem: nothing about the mappings will fix it, and this is where a
 * `DUPLICATES_DETECTED` surfaces now that SF-DUP's bypass toggle does not exist.
 */
export const SalesforceSyncLogsTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.salesforce.syncLogs.${key}`);

	const [entityFilter, setEntityFilter] = useState<string | undefined>(undefined);
	const [offset, setOffset] = useState(0);

	const { data, isLoading } = useGetSalesforceSyncLogsQuery({
		entityType: entityFilter,
		limit: LIMIT,
		offset,
	});

	const entityOptions: DropdownAutoCompleteItem[] = useMemo(
		() => [
			{ id: "ALL", title: ts("allEntities") },
			{ id: SalesforceEntityType.CONTACT, title: "Contact" },
			{ id: SalesforceEntityType.ACCOUNT, title: "Account" },
			{ id: SalesforceEntityType.LEAD, title: "Lead" },
			{ id: SalesforceEntityType.OPPORTUNITY, title: "Opportunity" },
		],
		[ts]
	);

	const handleEntityFilterChange = (value: DropdownAutoCompleteItem) => {
		setEntityFilter(value.id === "ALL" ? undefined : (value.id as string));
		setOffset(0);
	};

	if (isLoading) {
		// `Spinner` defaults to a white spinner, which is invisible on this white panel.
		return <LoadingBlock />;
	}

	const logs = data?.logs ?? [];
	const total = data?.total ?? 0;

	return (
		<div className="flex w-full flex-col gap-4">
			<div className="text-sm text-text-meta">{ts("description")}</div>

			<div className="w-full max-w-[250px]">
				<Dropdown
					data={entityOptions}
					defaultValue={entityOptions[0]}
					floating={true}
					handleSelect={handleEntityFilterChange}
					label={ts("filterByEntity")}
				/>
			</div>

			{logs.length === 0 ? (
				<div className="py-12 text-center text-sm text-text-meta">{ts("noLogs")}</div>
			) : (
				<div className="overflow-x-auto rounded-xl border border-border-light bg-brand-white">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-border-light bg-gray-50">
								<th className="px-4 py-3 text-left font-medium text-text-meta">
									{ts("entityType")}
								</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("status")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("synced")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("failed")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("error")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">
									{ts("startedAt")}
								</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">
									{ts("completedAt")}
								</th>
							</tr>
						</thead>
						<tbody>
							{logs.map((log) => (
								<tr key={log.id} className="border-b border-border-light last:border-b-0">
									<td className="px-4 py-3 text-text">{log.entityType}</td>
									<td className="px-4 py-3">
										<StatusBadge status={log.status} />
									</td>
									<td className="px-4 py-3 text-text">{log.recordsSynced ?? 0}</td>
									<td className="px-4 py-3 text-text">{log.recordsFailed ?? 0}</td>
									<td
										className="max-w-[240px] px-4 py-3 text-xs text-text-meta"
										data-testid={`salesforce-sync-log-error-${log.id}`}
									>
										<div className="truncate" title={describeSyncLogError(log)}>
											{describeSyncLogError(log)}
										</div>
										{isDuplicateRejection(log) && (
											<div className="mt-1 text-xs text-status-wrong-person">
												{t("company.salesforce.fieldMapping.preview.sync.duplicateHint")}
											</div>
										)}
									</td>
									<td className="px-4 py-3 text-text-meta">
										{log.createdAt ? formatDate(log.createdAt) : "—"}
									</td>
									<td className="px-4 py-3 text-text-meta">
										{log.completedAt ? formatDate(log.completedAt) : "—"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{total > LIMIT && (
				<div className="mt-2 flex justify-center gap-2">
					<button
						className="cursor-pointer rounded border border-border-light px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
						disabled={offset === 0}
						type="button"
						onClick={() => setOffset((prev) => Math.max(0, prev - LIMIT))}
					>
						{t("basics.previous")}
					</button>
					<button
						className="cursor-pointer rounded border border-border-light px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
						disabled={offset + LIMIT >= total}
						type="button"
						onClick={() => setOffset((prev) => prev + LIMIT)}
					>
						{t("basics.next")}
					</button>
				</div>
			)}
		</div>
	);
};
